import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import * as itemService from "../../Services/Http/ItemService";
import { useOrder } from "../../Context/useOrder";
import { Product } from "../../Models/Product";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import { Order } from "../../Models/Order";
import { dataContext } from "../../Context/contextProvider";
import { observer } from "mobx-react";
import ImgWithPlaceholder from "../../Components/ImgWithPlaceholder";
import { PackItemRequest } from "../../Models/PackItemRequest";
import { PackItemResponse } from "../../Models/PackItemResponse";
import { Button, Modal } from "react-bootstrap";
import ImageCarousel from "../../Components/ImageCarousel";
import OrderStatusBar from "../../Components/OrderStatusBar/OrderStatusBar";
import { useAuth } from "../../Context/useAuth";

interface Props {}

const successSound = new Audio();
successSound.src = "/assets/sounds/success.mp3";
successSound.load();

const errorSound = new Audio();
errorSound.src = "/assets/sounds/error.mp3";
errorSound.load();

const questionSound = new Audio();
questionSound.src = "/assets/sounds/question.mp3";
questionSound.load();

type ScanObject = {
  transNo?: string;
  detailNo?: number;
  stockCode?: string;
  originalQty: number;
  addedQty: number;
  totalQty: number;
  step: number;
  scanResponseStatus: ScanResponseStatusType;
};

type ScanResponseStatusType = "none" | "error" | "success";
const scanBarcodePlaceholderMessage = "Scan barcode";
const scanBarcodeOrQtyPlaceholderMessage = "Scan barcode or enter quantity";

const OrderPackPage = observer((props: Props) => {
  const { getCurrentOrder } = useOrder();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const searchBoxInputRef = useRef<HTMLInputElement>(null);
  const [modal1Open, setModal1Open] = useState<boolean>(false);
  const [modal2Open, setModal2Open] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | undefined>(undefined);
  const [scanBarcodePlaceholder, setScanBarcodePlaceholder] = useState<string>(scanBarcodePlaceholderMessage);
  const scanObject = useRef<ScanObject | null>(null);
  const navigate = useNavigate();
  const dataStore = useContext(dataContext);
  const { getSettingNumber } = useAuth();
  const [fullyPacked, setFullyPacked] = useState<boolean>(false);

  useLayoutEffect(() => {
    if (searchBoxInputRef.current) {
      searchBoxInputRef.current.focus();
    }
  }, []);

  const isFullyPacked = useCallback((order: Order): boolean => {
    if (!order || !order.cartLines) {
      return false;
    }
    const ispacked = order.cartLines.every((product) => {
      const picked: number = product.qty || 0;
      const packed: number = product.qtyPacked || 0;
      return picked === packed;
    });
    setFullyPacked(ispacked);
    return ispacked;
  }, []);

  const isFullyPicked = useCallback((order: Order | null = null): boolean => {
    if (!order || !order!.cartLines) {
      return false;
    }
    const incompleteLines = order.cartLines.find((product) => (product!.qty || 0) < (product!.qtyOrdered || 0));
    return !incompleteLines;
  }, []);

  const checkIfOrderComplete = useCallback(
    (order: Order): boolean => {
      if (!order || !order!.cartLines) {
        return true;
      }
      if (isFullyPacked(order)) {
        toast.info("This order is fully packed.");
        //navigate("/packlist");
        return true;
      }
      return false;
    },
    [isFullyPacked],
  );

  const scrollToProduct = (product: Product) => {
    if (product?.stockNo) {
      const element = document.getElementById(`stockno_${product.stockNo}`);
      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
        });
        element.classList.add("list-group-item-success");
        setTimeout(() => {
          element.classList.remove("list-group-item-success");
        }, 1500);
      }
      searchBoxInputRef.current?.focus();
    }
  };

  const sendPackRequest = useCallback(
    async (order: Order, product: Product) => {
      if (!scanObject.current) {
        return;
      }
      const packItemRequest: PackItemRequest = {
        transNo: scanObject.current.transNo,
        detailNo: scanObject.current.detailNo,
        stockCode: scanObject.current.stockCode,
        packQty: scanObject.current.totalQty,
        orderCompanyNo: order.companyNo,
      };
      const packItemResponse: PackItemResponse = await itemService.packAPI(packItemRequest, dataStore.serverUrl || "", dataStore.dbKey || "");
      setSearchTerm("");
      if (packItemResponse.error) {
        scanObject.current = {
          ...scanObject.current!,
          scanResponseStatus: "error",
        };
        setScanBarcodePlaceholder(scanBarcodePlaceholderMessage);
        errorSound.play();
        toast.error(packItemResponse.error, { autoClose: false });
        return;
      }
      if (packItemResponse.stockCode === scanObject.current.stockCode) {
        scanObject.current = {
          ...scanObject.current,
          step: scanObject.current.step + 1, // 2 = before second scan (qty digits scan), 3 = after qty scan
          scanResponseStatus: "success",
        };
        product.qtyPacked = packItemResponse.linePackQty || 0;
        successSound.play();
        toast.dismiss();
        toast.success(`Packed ${scanObject.current.addedQty}`, { autoClose: 200 });
        scrollToProduct(product);
        setScanBarcodePlaceholder(scanBarcodePlaceholderMessage);
        const isComplete: boolean = checkIfOrderComplete(order);
        if (!isComplete && scanObject.current && scanObject.current.scanResponseStatus === "success" && scanObject.current.step === 2) {
          setScanBarcodePlaceholder(scanBarcodeOrQtyPlaceholderMessage);
        } else if (scanObject.current.step === 3) {
          scanObject.current = null; // reset after successful packing
        }
      }
    },
    [checkIfOrderComplete, dataStore.dbKey, dataStore.serverUrl],
  );

  const doFirstScan = useCallback(
    async (barcode: string): Promise<void> => {
      try {
        scanObject.current = null; //reset

        const order = getCurrentOrder();
        if (!order) {
          setSearchTerm("");
          errorSound.play();
          toast.error("Order not found", { autoClose: 200 });
          return;
        }
        const foundCartLine = order!.cartLines!.find(
          (x) => (x.stockCode && x.stockCode.trim().toLowerCase() === barcode.trim().toLowerCase()) || (x.stockBarcode && x.stockBarcode === barcode.trim()),
        );
        if (!foundCartLine) {
          setSearchTerm("");
          errorSound.play();
          toast.error("Current order does not contain this product", {
            autoClose: 1000,
          });
          return;
        }

        const picked: number = foundCartLine.qty || 0;
        const packed: number = foundCartLine.qtyPacked || 0;

        if (picked <= packed) {
          setSearchTerm("");
          errorSound.play();
          toast.error("Packed quantity must not exceed Picked quantity", { autoClose: 2000 });
          return;
        }
        const addedQty: number = 1;
        const newQty: number = (foundCartLine.qtyPacked || 0) + addedQty;
        scanObject.current = {
          transNo: order.transNo,
          detailNo: foundCartLine.detailNo,
          stockCode: foundCartLine.stockCode,
          originalQty: foundCartLine.qtyPacked || 0,
          addedQty: addedQty,
          totalQty: newQty,
          step: 1, // 1 = before first scan
          scanResponseStatus: "none",
        };
        await sendPackRequest(order, foundCartLine);
      } catch (error: any) {
        scanObject.current = {
          ...scanObject.current!,
          scanResponseStatus: "error",
        };
        setScanBarcodePlaceholder(scanBarcodePlaceholderMessage);
        toast.error(error.message || error, { autoClose: false });
      }
    },
    [getCurrentOrder, sendPackRequest],
  );

  const doQtyScan = useCallback(
    async (enteredQty: number) => {
      if (enteredQty <= 0) {
        return;
      }
      const order = getCurrentOrder();
      if (!order) {
        setSearchTerm("");
        errorSound.play();
        toast.error("Order not found", { autoClose: 1000 });
        return;
      }
      const foundCartLine = order!.cartLines!.find((x) => x.stockCode === scanObject.current!.stockCode);
      if (!foundCartLine) {
        scanObject.current = null; //reset
        setSearchTerm("");
        errorSound.play();
        toast.error("Current order does not contain this product", {
          autoClose: 2000,
        });
        return;
      }
      const picked: number = foundCartLine.qty || 0;
      const packed: number = foundCartLine.qtyPacked || 0;

      if (picked <= packed) {
        setSearchTerm("");
        errorSound.play();
        toast.error("Packed quantity must not exceed Picked quantity", { autoClose: 2000 });
        return;
      }
      //if enteredQty is the same as scanned quantity 1
      if (enteredQty - scanObject.current!.addedQty <= 0) {
        setSearchTerm("");
        errorSound.play();
        toast.error("New quantity must higher than previous quantity", {
          autoClose: 2000,
        });
        return;
      }
      //if overpicked
      if (enteredQty + scanObject.current!.originalQty > picked) {
        setSearchTerm("");
        errorSound.play();
        toast.error("Scanned quantity exceeds Picked quantity", {
          autoClose: 2000,
        });
        return;
      }

      scanObject.current = {
        ...scanObject.current!,
        addedQty: enteredQty,
        totalQty: scanObject.current!.originalQty + enteredQty,
      };

      await sendPackRequest(order, foundCartLine);
    },
    [getCurrentOrder, sendPackRequest],
  );

  const getBarcodeQty = useCallback(
    (barcode: string): number => {
      if (!barcode || !barcode.trim() || isNaN(Number(barcode.trim()))) {
        return 0;
      }
      let amountOfDigits: number = getSettingNumber(dataStore.serverUrl!, dataStore.dbKey!, "PDAQtyDigits");
      if (amountOfDigits <= 0) {
        amountOfDigits = 2; // default to 2 digits if setting is not found or invalid
      }
      if (barcode.trim().length <= amountOfDigits) {
        return parseInt(barcode.trim(), 10);
      }
      return 0;
    },
    [dataStore.dbKey, dataStore.serverUrl, getSettingNumber],
  );

  const onBarcodeScanned = useCallback(
    (barcode: string): void => {
      const order = getCurrentOrder();
      if (order && isFullyPacked(order)) {
        toast.info("This order is fully packed.");
        return;
      }
      if (scanObject.current && scanObject.current.scanResponseStatus === "success" && scanObject.current.step === 2) {
        const enteredQty = getBarcodeQty(barcode);
        if (enteredQty > 0) {
          doQtyScan(enteredQty);
          return;
        }
      }
      doFirstScan(barcode);
    },
    [getCurrentOrder, isFullyPacked, doFirstScan, getBarcodeQty, doQtyScan],
  );

  const onKeyPressed = useCallback(
    (event: any) => {
      if (event.target && event.target instanceof HTMLInputElement && event.target.name === searchBoxInputRef.current!.name) {
        return;
      }
      const keyPressed = event.key ? event.key : "";
      if (keyPressed === "") {
        return;
      }
      if (keyPressed.toLowerCase() === "tab" || keyPressed.toLowerCase() === "enter") {
        event.preventDefault();
        setSearchTerm((oldState) => {
          const newState = oldState;
          onBarcodeScanned(newState);
          return newState;
        });
      } else if (keyPressed.length === 1) {
        setSearchTerm((oldState) => {
          const newState = oldState + keyPressed;
          return newState;
        });
      }
    },
    [onBarcodeScanned],
  );

  const bindGrid = useCallback(
    (order: Order | null = null) => {
      if (order) {
        if (isFullyPacked(order)) {
          toast.info("This order is fully packed.");
          return;
        }
      }
    },
    [isFullyPacked],
  );

  useEffect(() => {
    const order = getCurrentOrder();
    bindGrid(order);
    return () => {};
  }, [bindGrid, getCurrentOrder]);

  useEffect(() => {
    window.addEventListener("keydown", onKeyPressed);
    return () => {
      window.removeEventListener("keydown", onKeyPressed);
    };
  }, [onKeyPressed]);

  // useKeyDown((event: any) => {
  //     onKeyPressed(event);
  // }, []);

  const handleSearchChange = (event: any) => {
    setSearchTerm(event.target.value);
  };

  const handleKeyUp = (event: any) => {
    if (event.key && event.key.toLowerCase() === "enter") {
      event.preventDefault();
      onSearchClicked();
    }
  };

  const onSearchClicked = () => {
    onBarcodeScanned(searchTerm);
  };

  const getPickedCssClassName = (product: Product): string => {
    let pickedCssClassName = "d-flex qty-badge badge";
    if ((product.qtyOrdered || 0) === product.qty) {
      pickedCssClassName += " bg-success";
    } else {
      pickedCssClassName += " bg-danger";
    }
    return pickedCssClassName;
  };

  const getPackedCssClassName = (product: Product): string => {
    let packedCssClassName = "d-flex qty-badge badge me-2";
    const picked: number = product.qty || 0;
    const packed: number = product.qtyPacked || 0;

    if (picked === packed) {
      packedCssClassName += " bg-success";
    } else {
      packedCssClassName += " bg-secondary";
    }
    return packedCssClassName;
  };
  const onCloseModal1 = () => {
    setModal1Open(false);
    setSelectedProduct(undefined);
  };
  const handleContinueToBox = () => {
    if (!fullyPacked) {
      setModal2Open(true);
    } else {
      navigate("/box");
    }
  };
  const onCloseModal2 = () => {
    setModal2Open(false);
  };
  const onModalYesNo = (yes: boolean) => {
    setModal2Open(false);
    if (yes) {
      navigate("/box");
    }
  };
  return (
    <>
      <OrderStatusBar allowPrint={false} />
      <div className="container-limited-width order-pack-page">
        <div className="row sticky-top-3 search-bar">
          <div className="col-12 p-2">
            <div className="input-group">
              <input
                name="searchBox"
                type="text"
                className="form-control search-box"
                placeholder={scanBarcodePlaceholder}
                autoCapitalize="none"
                autoComplete="off"
                aria-describedby="btnGroupAddon"
                onKeyUp={handleKeyUp}
                onChange={handleSearchChange}
                value={searchTerm}
                ref={searchBoxInputRef}
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-12 my-1">
            {!isFullyPicked(getCurrentOrder()) && (
              <div className="d-flex justify-content-center text-danger fw-bold py-1" role="alert">
                WARNING - PART PICKED ORDER
              </div>
            )}
          </div>
        </div>
        <div className="row">
          <div className="col-12 mb-2 mt-2 p-0">
            <ul className="list-group list-group-flush p-0">
              {getCurrentOrder() &&
                getCurrentOrder()!.cartLines!.map((product, index) => {
                  return (
                    <li
                      id={`stockno_${product.stockNo}`}
                      key={`${product.stockNo}_${index}`}
                      className="list-group-item d-flex align-items-center p-1 product-list-item"
                      onClick={() => {
                        if (product.images) {
                          setSelectedProduct(product);
                          setModal1Open(true);
                        }
                      }}
                    >
                      <div className="d-flex flex-column product-image-container">
                        <div className="d-flex">
                          {product.images && product.images.length > 0 ? (
                            <ImgWithPlaceholder
                              src={product.images[0].thumbImageUrl}
                              alt={product.stockDesc}
                              className="product-image"
                              fallback={<img src="/assets/images/no-image-100x100.png" alt="" className="rounded product-image" />}
                            />
                          ) : null}
                        </div>
                        <div className="d-flex my-1 flex-grow-1">
                          <div className="d-flex flex-column" style={{ width: "100%" }}>
                            <div className="d-flex flex-row justify-content-start">
                              <div className={getPackedCssClassName(product)}>{product.qtyPacked || 0}</div>
                              <div className={getPickedCssClassName(product)}>
                                {product.qty!.toLocaleString(navigator.language, {
                                  maximumFractionDigits: 3,
                                })}
                                &nbsp;/&nbsp;
                                {(product.qtyOrdered || 0)!.toLocaleString(navigator.language, { maximumFractionDigits: 3 })}
                              </div>
                              <div className="d-flex pickunit align-items-center">{product.pickUnit}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex flex-column flex-grow-1 mx-2">
                        <div className="fw-bold">{product.stockDesc}</div>
                        <div className="d-flex flex-column">
                          <div className="text-muted">{product.stockCode}</div>
                          <div className="text-muted">Barcode: {product.stockBarcode}</div>
                        </div>
                      </div>
                    </li>
                  );
                })}
            </ul>
          </div>
        </div>
        <div className="row">
          <div className="col-12 d-flex mt-2 mb-4">
            <Button className="flex-fill m-1" variant={fullyPacked ? "success" : "secondary"} onClick={handleContinueToBox}>
              Box Weight & Labels
            </Button>
          </div>
        </div>
      </div>
      <Modal show={modal1Open} onHide={onCloseModal1} keyboard={true} fullscreen="xxl-down" aria-labelledby="contained-modal-title-vcenter" centered>
        <Modal.Header closeButton={true}>
          <Modal.Title>{selectedProduct?.stockDesc}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-column justify-content-center">
            {selectedProduct && selectedProduct.images && selectedProduct.images.length > 0 && (
              <ImageCarousel imageUrls={selectedProduct.images?.filter((i) => i !== undefined && i.largeImageUrl !== undefined)?.map((i) => i.largeImageUrl!) || []} alt={selectedProduct.stockDesc} />
            )}
            {selectedProduct && (
              <div className="d-flex flex-column flex-grow-1 fw-bold mt-3">
                <div className="text-muted">StockCode: {selectedProduct.stockCode}</div>
                <div className="text-muted">Barcode: {selectedProduct.stockBarcode}</div>
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
      <Modal show={modal2Open} onHide={onCloseModal2} backdrop="static" keyboard={false} centered>
        <Modal.Header>
          <Modal.Title>Warning</Modal.Title>
        </Modal.Header>
        <Modal.Body>Order is not fully packed. Continue ?</Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={() => onModalYesNo(true)}>
            Yes
          </Button>
          <Button variant="danger" onClick={() => onModalYesNo(false)}>
            No
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
});

export default OrderPackPage;
